import React, { useCallback, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'next-i18next';
import { HotFilterItem } from '@interfaces/api/responses/hot-filters.response';
import useWindowSize from '@hooks/use-window-size';
import {
  generateHotFiltersCheckedItemsUrlFragment,
  generateHotFiltersCheckedItemsUrlFragmentCategory,
  updateParamsWithHotFiltersTabs,
} from '@helpers/utils/hot-filters/hot-filters-url-helpers';
import {
  calculateHotFiltersTotalCheckedItems,
  getHotFiltersCheckedChildren,
  getHotFiltersMatchingTabs,
} from '@helpers/utils/hot-filters/hot-filters';
import { useHotFiltersContext } from '@context/hot-filters.context';
import {
  convertAppliedHotFiltersToPayload,
  getTranslatedHotFilterName,
  isAllowedHotFilterInUrlParams,
} from '@helpers/utils/hot-filters/hot-filters-parsing-util';
import { updateParamsInUrl } from '@helpers/routing';
import { PageType } from '@interfaces/common/common';
import HotFilterButton from '@components/organisms/hot-filters/hot-filter-button/hot-filter-button';
import HotFilterList from '@components/organisms/hot-filters/hot-filter-list/hot-filter-list';
import { useAnalytics } from '@context/analytics/analytics.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useBucket } from '@context/bucket.context';
import { useRouter } from 'next/router';
import HotFiltersLoading from './hot-filters-loading/hot-filters-loading';
import styles from './hot-filters.module.scss';

type HotFiltersProps = {
  appliedFilters: Record<string, string>;
  onApplyFilters: (
    filters: Record<string, string>,
    filtersWithoutCurrentApplied: Record<string, string>,
    currentAppliedFilter: Record<string, string>,
    filterKey: string,
    triggeredByClearFilters?: boolean,
  ) => void;
  handleOpenFilter: (filter: HotFilterItem) => void;
  isLoadingProducts?: boolean;
  pageType: PageType;
};

type ClearFiltersButtonProps = {
  t: TFunction;
  handleClearFilters: () => void;
  pageType: PageType;
};

const ClearFiltersButton: React.FC<ClearFiltersButtonProps> = ({ t, handleClearFilters, pageType }) => {
  return (
    <button
      className={styles.hotFilters__buttonsWrapper__clearButton}
      onClick={handleClearFilters}
      data-cy={`${pageType}-clear-filters`}
    >
      {t('FAVORITE_PAGE.FILTER_CTA.CLEAR_ALL_FILTERS')}
    </button>
  );
};

const HotFilterButtons = ({ hotFilters, getTranslatedHotFilterName, pageType, handleToggle, openFilters }) => {
  return hotFilters?.map((filter: HotFilterItem, index: number) => (
    <HotFilterButton
      key={`${filter.id}-${filter.name}-button`}
      label={getTranslatedHotFilterName(filter?.name)}
      dataCy={`${pageType}-hot-filter-${filter.id}-button`}
      onClick={() => handleToggle(index)}
      checkedItemCount={calculateHotFiltersTotalCheckedItems(filter) || 0}
      isSelected={openFilters.has(index)}
    />
  ));
};

const HotFilterLists = ({
  hotFilters,
  openFilters,
  getTabsData,
  handleClose,
  handleApply,
  updateCheckedCounts,
  pageType,
}) => {
  return hotFilters?.map(
    (filter: HotFilterItem, index: number) =>
      openFilters.has(index) && (
        <HotFilterList
          key={`${filter.id}-${filter.name}-list`}
          isDialogOpened={openFilters.has(index)}
          item={filter}
          tabsData={getTabsData(filter.id)}
          listIndex={index}
          onClose={() => handleClose(index)}
          onApply={(dataItem) => handleApply(dataItem, index)}
          updateCheckedCounts={updateCheckedCounts}
          dataCy={`${pageType}-hot-filter-${filter.id}`}
          showSearchBar={filter.id === 'brand' && !filter.isNested && filter?.children?.length > 1}
        />
      ),
  );
};

const DesktopHotFiltersUI = ({
  hotFilters,
  getTranslatedHotFilterName,
  pageType,
  handleToggle,
  openFilters,
  getTabsData,
  handleClose,
  handleApply,
  updateCheckedCounts,
}) => {
  return hotFilters?.map((filter: HotFilterItem, index: number) => (
    <li key={`${filter.id}-${filter.name}-container`}>
      <HotFilterButton
        key={`${filter.id}-${filter.name}-button`}
        label={getTranslatedHotFilterName(filter?.name)}
        dataCy={`${pageType}-hot-filter-${filter.id}-button`}
        onClick={() => handleToggle(index)}
        checkedItemCount={calculateHotFiltersTotalCheckedItems(filter) || 0}
        isSelected={openFilters.has(index)}
      />
      {openFilters.has(index) && (
        <HotFilterList
          key={`${filter.id}-${filter.name}-list`}
          isDialogOpened={openFilters.has(index)}
          item={filter}
          tabsData={getTabsData(filter.id)}
          listIndex={index}
          onClose={() => handleClose(index)}
          onApply={(dataItem) => handleApply(dataItem, index)}
          updateCheckedCounts={updateCheckedCounts}
          dataCy={`${pageType}-hot-filter-${filter.id}`}
          showSearchBar={filter.id === 'brand' && !filter.isNested && filter?.children?.length > 1}
        />
      )}
    </li>
  ));
};

const HotFilters: React.FC<HotFiltersProps> = ({
  appliedFilters,
  onApplyFilters,
  handleOpenFilter,
  pageType,
  isLoadingProducts,
}) => {
  const { t } = useTranslation();
  const { isWiderThanMd } = useWindowSize();
  const { updateDataLayer } = useAnalytics();
  const { sendAnalyticEvent } = useAnalyticEvents('favorites');
  const { isFeatureEnabled } = useBucket();
  const router = useRouter();

  const { sortBy } = router?.query as {
    sortBy?: string;
  };

  const [, setCheckedCounts] = useState<Map<number, number>>(new Map());
  const [openFilters, setOpenFilters] = useState<Map<number, boolean>>(new Map());
  const { hotFilters: availableHotFilters, setHotFilters, totalCount, isUserSlug } = useHotFiltersContext();

  const tabsData = useMemo(() => {
    return availableHotFilters.find((hotFilter: HotFilterItem) => hotFilter.filterId === 'universe');
  }, [availableHotFilters]);

  const hotFilters = useMemo(() => {
    return availableHotFilters.filter((hotFilter: HotFilterItem) => hotFilter.filterId !== 'universe');
  }, [availableHotFilters]);

  const hasActiveFilters = useMemo(() => {
    return (
      availableHotFilters.reduce((hasCheckedChild, filter) => {
        if (hasCheckedChild) {
          return true;
        }

        // Check if any of the filter's children is checked
        return filter.children?.some((child) => child.isChecked) || false;
      }, false) && isFeatureEnabled((f) => !!f.favoritesFiltersClearAll, true)
    );
  }, [availableHotFilters]);

  const updateCheckedCounts = useCallback((index: number, count: number) => {
    setCheckedCounts((prevCounts) => {
      const newCounts = new Map(prevCounts);
      newCounts.set(index, count);
      return newCounts;
    });
  }, []);

  const handleToggle = useCallback(
    (index) => {
      setOpenFilters((prevState) => {
        const newState = new Map(prevState);
        const isOpened = !newState.get(index);

        if (isOpened) {
          const prevOpenedIndex = Array.from(newState.keys()).find((key) => newState.get(key));
          if (prevOpenedIndex !== undefined) {
            newState.set(prevOpenedIndex, false);
          }

          newState.set(index, true);
          handleOpenFilter(hotFilters[index]);
        } else {
          newState.set(index, false);
          handleOpenFilter(null);
        }

        return newState;
      });
    },
    [hotFilters, handleOpenFilter],
  );

  const handleClose = useCallback((index) => {
    setOpenFilters((prevState) => {
      const newState = new Map(prevState);
      newState.delete(index);

      handleOpenFilter(null);

      return newState;
    });
  }, []);

  const getTabsData = useCallback(
    (id: string) => {
      return id === 'category' ? tabsData : null;
    },
    [tabsData],
  );

  const handleApply = (dataItem: HotFilterItem, index: number) => {
    const resultString =
      dataItem?.id === 'category'
        ? generateHotFiltersCheckedItemsUrlFragmentCategory(dataItem?.children)
        : generateHotFiltersCheckedItemsUrlFragment(dataItem?.children);
    const params = { ...appliedFilters, [dataItem.id]: resultString };

    // Retrieves tabs based on the provided data item ID, filters checked children,
    // finds matching tabs, and updates URL parameters with the filtered hot filters.
    const tabs = getTabsData(dataItem?.id);
    if (tabs) {
      const checkedChildren = getHotFiltersCheckedChildren(dataItem?.children);
      const matchingTabs = getHotFiltersMatchingTabs(checkedChildren, tabs);
      updateParamsWithHotFiltersTabs(params, tabs, matchingTabs);
    }

    // https://github.com/vercel/next.js/discussions/18072
    updateParamsInUrl(params);
    onApplyFilters(params, { ...appliedFilters }, { [dataItem.id]: resultString }, dataItem.id, false);
    handleClose(index);
  };

  const trackClearFilters = (): void => {
    const searchParams = new URLSearchParams(window.location.search);
    const filtersFromURL = Object.fromEntries(searchParams.entries());
    const isHotFiltersEnabledAndInUrl = isAllowedHotFilterInUrlParams(filtersFromURL);

    if (totalCount !== null || isHotFiltersEnabledAndInUrl) {
      const analyticData = {
        category: isUserSlug ? 'my_favourites' : 'member_favourites',
        sorting: sortBy,
        nb_items: totalCount?.toString() || '0',
        filters_details: JSON.stringify(convertAppliedHotFiltersToPayload(filtersFromURL)),
      };

      updateDataLayer(analyticData);
      sendAnalyticEvent('favorites_filter_clear_all', analyticData);
    }
  };

  const resetFilters = (): void => {
    setHotFilters(hotFilters);

    const updatedUrlParams = availableHotFilters.reduce(
      (acc, filter) => {
        acc[filter.id] = '';
        return acc;
      },
      { pageSize: '', page: '' },
    );

    updateParamsInUrl(updatedUrlParams);
    setCheckedCounts(new Map());
    setOpenFilters(new Map());
    onApplyFilters({}, {}, {}, '', true);
  };

  const handleClearFilters = (): void => {
    trackClearFilters();
    resetFilters();
  };

  if (isLoadingProducts) {
    return <HotFiltersLoading />;
  }

  return isWiderThanMd ? (
    <>
      <DesktopHotFiltersUI
        hotFilters={hotFilters}
        getTranslatedHotFilterName={getTranslatedHotFilterName}
        pageType={pageType}
        handleToggle={handleToggle}
        openFilters={openFilters}
        getTabsData={getTabsData}
        handleClose={handleClose}
        handleApply={handleApply}
        updateCheckedCounts={updateCheckedCounts}
      />
      {hasActiveFilters && (
        <ClearFiltersButton
          t={t}
          pageType={pageType}
          handleClearFilters={handleClearFilters}
        />
      )}
    </>
  ) : (
    <>
      <div className={styles.hotFilters__buttonsWrapper}>
        <HotFilterButtons
          hotFilters={hotFilters}
          getTranslatedHotFilterName={getTranslatedHotFilterName}
          pageType={pageType}
          handleToggle={handleToggle}
          openFilters={openFilters}
        />
        {hasActiveFilters && (
          <ClearFiltersButton
            t={t}
            pageType={pageType}
            handleClearFilters={handleClearFilters}
          />
        )}
      </div>
      <HotFilterLists
        hotFilters={hotFilters}
        openFilters={openFilters}
        getTabsData={getTabsData}
        handleClose={handleClose}
        handleApply={handleApply}
        updateCheckedCounts={updateCheckedCounts}
        pageType={pageType}
      />
    </>
  );
};

export default HotFilters;
